import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import "./AppBar.css";
import TaskPopup from "../pages/Tasks/TaskPopup";

const SectionAppBar = ({
  title = "null",
  path,
  isSection = false,
  partId,
  revisionId,
  sectionId = "null",
  isStep = false,
  stepId,
  isPart = false,
  substepId = "null",
  isSubstep = false,
  partNumber = "",
  revision = "",
  section = "",
  stepTitle = "",
  richCount = 0,
  subsCount = 0,
  attachmentCount = 0,
  videoCount = 0,
  formCount = 0,
  canvasCount = 0,
  stepCount = 0,
  sectionCount = 0,
  selectedSubstepTitle = "",
  siblingOrder = null,
  elementType,
  buttons = [],
  isSelectedPage = false,
  isSectionElement = false,
  id = "",
  partType,
  description,
  status,
  steps,
  data,
  fromTreeId,
  fromSiblingOrder,
  fromStepSiblingOrder,
  fromStepTreeId,
  fromSubstepTreeId,
  fromSubstepSiblingOrder,
  editSubstepbtn = false,
  stepSibling,
  lastChildSibling,
  lastSubstepSibling,
  move_rev_node_info = {},
  item_code_type = "",
}) => {
  const [showTasks, setShowTasks] = useState(false);
  const [showAddDropdown, setShowAddDropdown] = useState(false);

  const navigate = useNavigate();

  // Get sections from Redux store to calculate sourceSectionIndex
  const { sections } = useSelector((state) => state.section);

  // Function to calculate sourceSectionIndex for steps
  const calculateSourceSectionIndex = () => {
    if (!isStep || !sectionId || !sections || sections.length === 0) {
      return data?.sourceSectionIndex;
    }

    // Sort sections by sibling_order to get the correct order
    const sortedSections = [...sections].sort((a, b) => {
      const orderA = parseInt(a.sibling_order || "0", 10);
      const orderB = parseInt(b.sibling_order || "0", 10);
      return orderA - orderB;
    });

    // Find the section that contains the current step
    const sectionIndex = sortedSections.findIndex(
      (s) => s.section_tree_node_id === sectionId
    );

    return sectionIndex >= 0 ? sectionIndex : data?.sourceSectionIndex;
  };

  // Function to clear all preview data keys
  const clearPreviewDataKeys = () => {
    try {
      const keys = Object.keys(localStorage);
      const sessionKeys = Object.keys(sessionStorage);

      // Clear preview backup keys
      keys.forEach((key) => {
        if (key.startsWith("previewBackup_")) {
          localStorage.removeItem(key);
          console.log("SectionAppBar: Cleared preview backup key:", key);
        }
      });

      // Clear preview form data keys from sessionStorage
      sessionKeys.forEach((key) => {
        if (key.startsWith("previewFormData_")) {
          sessionStorage.removeItem(key);
          console.log("SectionAppBar: Cleared preview form data key:", key);
        }
      });

      // Clear window object keys
      Object.keys(window).forEach((key) => {
        if (key.startsWith("previewFormData_")) {
          delete window[key];
          console.log("SectionAppBar: Cleared window preview key:", key);
        }
      });

      // Clear sessionStorage flags
      sessionStorage.removeItem("cameFromPreview");
      sessionStorage.removeItem("preserveFormData");

      console.log("SectionAppBar: Cleared all preview data keys");
    } catch (e) {
      console.warn("Failed to clear preview data keys:", e);
    }
  };

  const addElementToSection = (sectionId, type) => {
    const newElement = { id: Date.now(), type, details: {} };

    // Function to calculate next available section number
    const getNextSectionNumber = () => {
      if (type.toLowerCase().includes("section")) {
        // Get all existing section names and find the highest number
        const sectionNumbers = [];

        // Check if we have access to sections data
        if (window.sectionsData && Array.isArray(window.sectionsData)) {
          // Extract section numbers from existing sections
          window.sectionsData.forEach((section) => {
            if (section.section_name) {
              const match = section.section_name.match(/Section\s+(\d+)/i);
              if (match) {
                sectionNumbers.push(parseInt(match[1]));
              }
            }
          });
        }

        // If no sections data available, fall back to sectionCount + 1
        if (sectionNumbers.length === 0) {
          return parseInt(sectionCount) + 1;
        }

        // Find the highest section number and add 1
        const maxSectionNumber = Math.max(...sectionNumbers);
        return maxSectionNumber + 1;
      }
      return null;
    };

    // Navigate to the corresponding page based on the type
    let elementTitle = "";
    const elementTypeLower = type.toLowerCase();
    const isSubstepType = elementTypeLower.includes("subs");
    if (elementTypeLower.includes("rich")) {
      elementTitle = "Rich Text " + (parseInt(richCount) + 1);
    } else if (isSubstepType) {
      // Compute next substep label globally for the step (e.g., Substep 1D)
      const stepNumberMatch = (stepTitle || "").match(/Step\s+(\d+)/i);
      let computedStepNumber = stepNumberMatch ? stepNumberMatch[1] : "";
      if (!computedStepNumber && selectedSubstepTitle) {
        const numMatch = (selectedSubstepTitle || "").match(/(\d+)/);
        if (numMatch) computedStepNumber = numMatch[1];
      }

      // When adding from a selected substep, calculate alphabetical position
      let nextIndex;
      if (isSubstep && selectedSubstepTitle) {
        // Extract the letter from the current substep (e.g., "3A" -> "A")
        const currentSubstepMatch = selectedSubstepTitle.match(/(\d+)([A-Z])/);
        if (currentSubstepMatch) {
          const currentLetter = currentSubstepMatch[2];
          const currentLetterIndex = currentLetter.charCodeAt(0) - 64; // A=1, B=2, etc.
          nextIndex = currentLetterIndex + 1; // Next letter after current
        } else {
          // Fallback to existing logic if pattern doesn't match
          const nextIndexRaw = parseInt(lastSubstepSibling, 10);
          const fallbackIndex = (parseInt(subsCount, 10) || 0) + 1;
          nextIndex =
            Number.isFinite(nextIndexRaw) && nextIndexRaw > 0
              ? nextIndexRaw
              : fallbackIndex;
        }
      } else {
        // Default behavior for adding from step level
        const nextIndexRaw = parseInt(lastSubstepSibling, 10);
        const fallbackIndex = (parseInt(subsCount, 10) || 0) + 1;
        nextIndex =
          Number.isFinite(nextIndexRaw) && nextIndexRaw > 0
            ? nextIndexRaw
            : fallbackIndex;
      }

      const nextLetter = String.fromCharCode(64 + Math.max(1, nextIndex));
      elementTitle = `Substep ${computedStepNumber}${nextLetter}`;
    } else if (elementTypeLower.includes("form")) {
      elementTitle = "Form " + (parseInt(formCount) + 1);
    } else if (elementTypeLower.includes("video")) {
      elementTitle = "Video " + (parseInt(videoCount) + 1);
    } else if (elementTypeLower.includes("attachment")) {
      elementTitle = "Attachment " + (parseInt(attachmentCount) + 1);
    } else if (elementTypeLower.includes("canvas")) {
      elementTitle = "Canvas " + (parseInt(canvasCount) + 1);
    } else if (elementTypeLower.includes("step")) {
      elementTitle = "Step " + (parseInt(stepCount) + 1);
      stepTitle = elementTitle;
    } else if (elementTypeLower.includes("section")) {
      const nextSectionNumber = getNextSectionNumber();
      elementTitle = `Section ${nextSectionNumber}`;
    }

    console.log("element ", elementTitle);

    // if (isEdit) {
    //   siblingOrder = parseInt(siblingOrder.split(",")[0].trim()) - 1;
    // }

    const isStepType = type.toLowerCase() === "step";
    const normalizedType = type.toLowerCase().replace(/\s+/g, "-");

    // Determine sibling order index to use
    const parts =
      typeof siblingOrder === "string" ? siblingOrder.split(",") : [];
    const rawSiblingOrder =
      (isSection && isStepType) ||
      (isStep && normalizedType !== "step") ||
      (isSubstep && !isSubstepType)
        ? parts[1]
        : parts[0];

    let selectedSiblingOrder =
      rawSiblingOrder && rawSiblingOrder.trim() !== ""
        ? rawSiblingOrder.trim()
        : siblingOrder;

    // When adding a substep from a selected substep, calculate the proper sibling order
    // to insert it alphabetically after the current substep
    // if (
    //   isSubstep &&
    //   isSubstepType &&
    //   selectedSubstepTitle &&
    //   selectedSiblingOrder !== "null"
    // ) {
    //   const currentSiblingOrder = parseFloat(selectedSiblingOrder);
    //   if (!isNaN(currentSiblingOrder)) {
    //     // Use a very small increment based on timestamp to ensure uniqueness
    //     // and proper ordering. This places the new substep immediately after the current one.
    //     const timestamp = Date.now();
    //     const microIncrement = (timestamp % 1000) / 10000; // Creates 0.0001 to 0.0999
    //     const newSiblingOrder = currentSiblingOrder + 0.01 + microIncrement;
    //     selectedSiblingOrder = newSiblingOrder.toString();
    //   }
    // }

    // Determine step title
    const resolvedStepTitle =
      isSubstep && isSubstepType
        ? stepTitle.split(">")[0].trim()
        : isStep && normalizedType !== "step"
        ? title
        : stepTitle;

    let path;
    if (isPart || (isSection && !isStepType)) {
      path = `/add-section/${partId}/${revisionId}/${partNumber}/${revision}/${
        isPart ? section : elementTitle || section || "null"
      }/${sectionId}/${siblingOrder.split(",")[0].trim()}/${isPart}/${
        elementTitle || "Section 2"
      }`;
    } else {
      path = `/section/${sectionId}/${normalizedType}/${
        newElement.id
      }/${partId}/${revisionId}/${stepId}/${substepId}/${partNumber}/${
        title || "null"
      }/${elementTitle}/${revision}/${
        isSubstepType ? stepTitle.split(">")[0].trim() : stepTitle
      }/${isStepType && isSection ? title : section}/${
        (isStep && isStepType) || isSectionElement
          ? siblingOrder
          : isSection && elementTypeLower.includes("step")
          ? stepSibling
          : (isStep || isSubstep) && isSubstepType
          ? isSubstep && isSubstepType
            ? selectedSiblingOrder && selectedSiblingOrder !== "null"
              ? parseInt(selectedSiblingOrder).toString()
              : "1"
            : lastSubstepSibling
          : lastChildSibling
      }`;
    }

    // Clear Canvas related localStorage and IndexedDB data
    localStorage.removeItem("canvasState");
    localStorage.removeItem("canvasSnapshot");
    sessionStorage.removeItem("canvasStateInfo");
    localStorage.removeItem("pageId");
    localStorage.removeItem("fromMyCanvas");
    localStorage.removeItem("elementCounts");
    localStorage.removeItem("canvasPages");
    localStorage.removeItem("canvas_name");
    localStorage.removeItem("canvas_category_name");
    localStorage.removeItem("canvas_category_id");

    // Clear canvasElementsDB IndexedDB
    try {
      window.indexedDB.deleteDatabase("canvasElementsDB");
      console.log("SectionAppBar: Cleared canvasElementsDB");
    } catch (e) {
      console.warn("Failed to clear canvasElementsDB:", e);
    }

    // Only clear rich text related localStorage if not navigating to rich text pages
    const isRichTextNavigation =
      path.includes("/section/rich-text") ||
      path.includes("/edit-rich-text") ||
      (path.includes("/add-section") && type?.toLowerCase().includes("rich"));

    console.log("SectionAppBar: Navigation check", {
      path,
      type,
      isRichTextNavigation,
      willClearRichTextData: !isRichTextNavigation,
    });

    if (!isRichTextNavigation) {
      console.log("SectionAppBar: Clearing rich text localStorage data");
      localStorage.removeItem("short_description");
      localStorage.removeItem("content");
      localStorage.removeItem("category_id");
      localStorage.removeItem("category_name");
    } else {
      console.log("SectionAppBar: Preserving rich text localStorage data");
    }

    // Clear Video related localStorage and IndexedDB data
    localStorage.removeItem("attachments");
    localStorage.removeItem("video");
    localStorage.removeItem("video_url");
    localStorage.removeItem("current_video_id");
    localStorage.removeItem("short_description");

    // Clear VideoStorageDB IndexedDB
    try {
      window.indexedDB.deleteDatabase("VideoStorageDB");
      console.log("SectionAppBar: Cleared VideoStorageDB");
    } catch (e) {
      console.warn("Failed to clear VideoStorageDB:", e);
    }

    // Clear AddSection and EditSection localStorage data
    // But NOT when coming back from preview
    try {
      const keys = Object.keys(localStorage);
      const currentPath = window.location.pathname;
      const isComingFromPreview =
        currentPath.includes("/preview") ||
        (document.referrer && document.referrer.includes("/preview")) ||
        sessionStorage.getItem("cameFromPreview") ||
        window.history.state?.cameFromPreview ||
        sessionStorage.getItem("preserveFormData");

      if (!isComingFromPreview) {
        // Clear AddSection data
        keys.forEach((key) => {
          if (key.startsWith("addSection_")) {
            localStorage.removeItem(key);
            console.log("SectionAppBar: Cleared AddSection data:", key);
          }
        });

        // Clear EditSection data
        keys.forEach((key) => {
          if (key.startsWith("editSection_")) {
            localStorage.removeItem(key);
            console.log("SectionAppBar: Cleared EditSection data:", key);
          }
        });
      } else {
        console.log(
          "SectionAppBar: Preserving AddSection/EditSection data (coming from preview)"
        );
        // Clear the flags for next time
        sessionStorage.removeItem("cameFromPreview");
        sessionStorage.removeItem("preserveFormData");
      }
    } catch (e) {
      console.warn("Failed to clear AddSection/EditSection localStorage:", e);
    }

    // Clear preview data keys when navigating to AddElementToSection or EditSection
    clearPreviewDataKeys();

    // Clear bulletproof video data when adding a new video element
    if (elementTypeLower.includes("video")) {
      console.log(
        "SectionAppBar: Clearing bulletproof video data for new video element"
      );

      // Clear global video backup
      if (window.videoBackup) {
        delete window.videoBackup;
        console.log("SectionAppBar: Cleared window.videoBackup");
      }

      // Clear Redux video data (if store is available)
      if (window.__REDUX_STORE__) {
        try {
          window.__REDUX_STORE__.dispatch({
            type: "video/clearTempVideoData",
          });
          console.log("SectionAppBar: Cleared Redux video data");
        } catch (e) {
          console.warn("SectionAppBar: Could not clear Redux video data:", e);
        }
      }

      // Clear all bulletproof video localStorage keys
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (
          key.startsWith("BULLETPROOF_VIDEO_") ||
          key === "CURRENT_VIDEO_SESSION"
        ) {
          localStorage.removeItem(key);
          console.log("SectionAppBar: Cleared bulletproof video key:", key);
        }
      });
    }

    console.log("Redirecting with Data", data);
    navigate(path, {
      state: {
        data,
        partNumbers: data?.partNumbers || [],
        sourceSubstepIndex: data?.sourceSubstepIndex,
        sourceStepId: data?.sourceStepId || stepId,
        sourceNodeId: data?.sourceNodeId || id,
        targetSubstepIndex: data?.targetSubstepIndex,
        targetStepId: data?.targetStepId,
        targetNodeId: data?.targetNodeId,
        status: data?.status || status,
        partNumber: data?.partNumber || partNumber,
        description: data?.description || description,
        type: data?.type || type || partType,
        sourceNodeIndex: data?.sourceNodeIndex,
        sourceSectionIndex: data?.sourceSectionIndex,
        sourceStepIndex: data?.sourceStepIndex,
        sourceNodeType: elementType,
        subsCount,
        attachmentCount,
        videoCount,
        formCount,
        richCount,
        canvasCount,
        richTextNumber:
          elementType === "Rich Text"
            ? title.match(/Rich Text\s+(\d+)/i)?.[1]
            : undefined,
        stepNumber: title.includes("Step")
          ? title.match(/Step\s+(\d+)/i)?.[1]
          : undefined,
        move_rev_node_info,
        item_code_type,
      },
    });
  };

  const navigateToSelectProcedureScreen = () => {
    // Debug logging to track section data
    console.log(
      "SectionAppBar: navigateToSelectProcedureScreen - section data:",
      {
        section: section,
        dataSection: data.section,
        dataSectionName: data.sectionName,
        fallbackSection: section || data.section || data.sectionName,
      }
    );

    const elementTypeNormalized = elementType
      ?.toLowerCase()
      .replace(/\s+/g, "-");
    const [first = 0, second = 0] = (siblingOrder ?? "")
      .toString()
      .split(",")
      .map((s) => parseInt(s.trim(), 10));

    const shouldUseSecond =
      (isStep && elementTypeNormalized !== "step") ||
      (isSubstep && !elementType?.toLowerCase().includes("subs"));

    const siblingIndex = (shouldUseSecond ? second : first) - 1;

    const increamentedTitle = "test";

    // Clear preview data keys when navigating to procedure selection
    clearPreviewDataKeys();

    // Use the correct data based on the current element type
    // If newData exists (which contains the actual element data), use it
    // Otherwise fall back to the original data
    const currentElementData = data.newData || data;

    // Construct the substep object properly for substep pages
    let substepObject = { ...data, ...data.newData };
    if (isSubstep && !substepObject) {
      // For substep pages, construct the substep object from available data
      substepObject = {
        sub_step_tree_node_id: data.fromSubstepId || data.id,
        short_description: data.short_description,
        sibling_order: data.fromSubstepSiblingOrder || data.sibling_order,
        procedure_tree_ref: data.fromSubstepTreeId || data.fromTreeId,
        created_at: data.created_at,
        updated_at: data.updated_at,
        ...data,

        // Add other necessary substep properties
      };
    }

    // Ensure we have the proper substep structure for API results
    if (isSubstep && substepObject && !substepObject.sub_step_tree_node_id) {
      // If we have data but missing sub_step_tree_node_id, try to construct it
      substepObject = {
        ...substepObject,
        sub_step_tree_node_id:
          substepObject.id || data.fromSubstepId || data.id,
        short_description:
          substepObject.short_description || substepObject.description,
        sibling_order:
          substepObject.sibling_order ||
          data.fromSubstepSiblingOrder ||
          data.sibling_order,
        procedure_tree_ref:
          substepObject.procedure_tree_ref ||
          substepObject.tree_node_id ||
          data.fromSubstepTreeId ||
          data.fromTreeId,
        created_at: substepObject.created_at,
        updated_at: substepObject.updated_at,
      };
    }

    navigate("/procedure-copy-select-procedure", {
      state: {
        siblingOrder: siblingIndex,
        elementType,
        id,
        title: currentElementData.title
          ? currentElementData.title
          : title.toLowerCase().includes("subs")
          ? selectedSubstepTitle
          : title,
        type: partType || currentElementData.type,
        description: description,
        revision,
        partNumber,
        status: data.status,
        steps: data.steps || steps,
        sections: data.sections || [],
        childTreeId: id,
        sourceSubstepIndex: data.sourceSubstepIndex,
        sourceStepId: data.sourceStepId,
        sourceNodeId: data.sourceNodeId,
        targetSubstepIndex: data.targetSubstepIndex,
        targetStepId: data.targetStepId,
        targetNodeId: data.targetNodeId,
        sourceNodeIndex: data.sourceNodeIndex || data.childIndex,
        sourceSectionIndex: calculateSourceSectionIndex(),
        sourceStepIndex: data?.sourceStepIndex || data?.stepIndex,
        revisionId,
        fromTreeId,
        fromSectionId: sectionId,
        fromSiblingOrder: data.fromSiblingOrder,
        partId,
        fromStepId: stepId,
        fromStepSiblingOrder: fromStepSiblingOrder || data.fromStepSiblingOrder,
        fromStepTreeId,
        fromSubstepTreeId,
        fromSubstepLetter: data.fromSubstepLetter,
        fromSubstepId: substepId,
        fromSubstepSiblingOrder:
          fromSubstepSiblingOrder ||
          data.fromSubstepSiblingOrder ||
          data.sibling_order ||
          data.procedure_tree?.sibling_order,
        elementId:
          currentElementData.rich_text_node_id ||
          currentElementData.attachment_tree_node_id ||
          currentElementData.video_tree_node_id ||
          currentElementData.video_tree_node_ref ||
          currentElementData.canvas_tree_node_id,
        elementTreeId:
          currentElementData.procedure_tree_ref || data.procedure_tree_ref,
        section: data.section || data.sectionName || section, // Ensure section has fallback values
        sectionDescription: data.sectionDescription, // Add section description
        richTextsByStep: data.richTextsByStep || data.richTexts,
        attachmentsByStep: data.attachmentsByStep || data.attachments,
        videosByStep: data.videosByStep || data.videos,
        formsByStep: data.formsByStep || data.forms,
        canvasesByStep: data.canvasesByStep || data.canvases,
        step: data.step,
        substep: substepObject, // Use the properly constructed substep object
        richTexts: data.richTexts,
        forms: data.forms,
        attachments: data.attachments,
        videos: data.videos,
        node: currentElementData, // Use the current element data instead of data.node
        nodeType: currentElementData.type || data.nodeType,
        fromNodeId: data.fromNodeId,
        richTexts: data.richTexts || data.richTextsBySubstep,
        forms: data.forms || data.formsBySubstep,
        attachments: data.attachments || data.attachmentsBySubstep,
        videos: data.videos || data.videosBySubstep,
        canvases: data.canvases || data.canvasesBySubstep,
        substepsByStep: data.substepsByStep,
        // Add context information for better routing
        isSectionElement,
        isSection,
        isStep,
        isSubstep,
        isPart,
      },
    });
  };
  return (
    <>
      <header
        className="appbar"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px",
        }}
      >
        {/* Left Side: Cancel & Task Buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <button
            className="cancel-button"
            onClick={() => {
              // window.history.back();
              const description = data?.description || "";
              const safeDescription = description
                ? encodeURIComponent(description)
                : "-";

              navigate(
                `/revision-submission/${partId}/${revision}/${
                  partType || data?.partType || data?.type
                }/${partNumber}/${revisionId}/${safeDescription}`,
                {
                  replace: true, // Replaces current entry in history stack
                  state: {
                    status,
                  },
                }
              );
            }}
          >
            Cancel
          </button>

          <button className="appbar-button" onClick={() => setShowTasks(true)}>
            + Task
          </button>

          <button
            className="appbar-button tables-button"
            onClick={() => setShowAddDropdown(!showAddDropdown)}
          >
            Add +
          </button>

          {showAddDropdown && (
            <div
              className="dropdown-menu"
              style={{
                position: "absolute",
                backgroundColor: "#f9f9f9",
                boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                padding: "10px",
                borderRadius: "5px",
                marginTop: "5px",
                left: "0",
              }}
            >
              {isPart
                ? ["Section"].map((type) => (
                    <button
                      key={type}
                      className="dropdown-item"
                      onClick={() => addElementToSection(sectionId, type)}
                      style={{
                        padding: "8px 16px",
                        cursor: "pointer",
                        border: "none",
                        backgroundColor: "transparent",
                        width: "100%",
                        textAlign: "left",
                      }}
                    >
                      + {type}
                    </button>
                  ))
                : isSubstep
                ? [
                    "Substep",
                    "Attachment",
                    "Canvas",
                    "Form",
                    "Rich Text",
                    "Video",
                  ].map((type) => (
                    <button
                      key={type}
                      className="dropdown-item"
                      onClick={() => addElementToSection(sectionId, type)}
                      style={{
                        padding: "8px 16px",
                        cursor: "pointer",
                        border: "none",
                        backgroundColor: "transparent",
                        width: "100%",
                        textAlign: "left",
                      }}
                    >
                      + {type}
                    </button>
                  ))
                : isStep
                ? [
                    "Step",
                    "Substep",
                    "Attachment",
                    "Canvas",
                    "Form",
                    "Rich Text",
                    "Video",
                  ].map((type) => (
                    <button
                      key={type}
                      className="dropdown-item"
                      onClick={() => addElementToSection(sectionId, type)}
                      style={{
                        padding: "8px 16px",
                        cursor: "pointer",
                        border: "none",
                        backgroundColor: "transparent",
                        width: "100%",
                        textAlign: "left",
                      }}
                    >
                      + {type}
                    </button>
                  ))
                : isSection
                ? ["Section", "Step"].map((type) => (
                    <button
                      key={type}
                      className="dropdown-item"
                      onClick={() => addElementToSection(sectionId, type)}
                      style={{
                        padding: "8px 16px",
                        cursor: "pointer",
                        border: "none",
                        backgroundColor: "transparent",
                        width: "100%",
                        textAlign: "left",
                      }}
                    >
                      + {type}
                    </button>
                  ))
                : ["Attachment", "Canvas", "Form", "Rich Text", "Video"].map(
                    (type) => (
                      <button
                        key={type}
                        className="dropdown-item"
                        onClick={() => addElementToSection(sectionId, type)}
                        style={{
                          padding: "8px 16px",
                          cursor: "pointer",
                          border: "none",
                          backgroundColor: "transparent",
                          width: "100%",
                          textAlign: "left",
                        }}
                      >
                        + {type}
                      </button>
                    )
                  )}
            </div>
          )}
        </div>

        {/* Centered Title */}
        <div style={{ flexGrow: 1, textAlign: "center" }}>
          <h1
            className="section-element-title"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {(() => {
              const rawTitle = title.toLowerCase().includes("subs")
                ? selectedSubstepTitle
                : title;

              const cleanedTitle = rawTitle.replace(
                /^(.*?)\s+\1\s+(\d+)$/i,
                "$1 $2"
              );

              return `${cleanedTitle} Selected`;
            })()}

            {isPart && (
              <button
                className="appbar-button"
                onClick={() => {
                  sessionStorage.removeItem(`editRevisionDraft_${revisionId}`);
                  navigate(`/edit-procedure-revision/${revisionId}`, {
                    state: {
                      data: {
                        siblingOrder: siblingOrder || data.siblingOrder,
                        ...data,
                      },
                    },
                  });
                }}
              >
                Edit
              </button>
            )}

            {isSelectedPage && (
              <>
                {isSection && !isPart && sectionId && (
                  <button
                    className="appbar-button"
                    onClick={() => {
                      // Clear AddSection and EditSection localStorage data
                      try {
                        const keys = Object.keys(localStorage);

                        // Clear AddSection data
                        keys.forEach((key) => {
                          if (key.startsWith("addSection_")) {
                            localStorage.removeItem(key);
                            console.log(
                              "SectionAppBar: Cleared AddSection data:",
                              key
                            );
                          }
                        });

                        // Clear EditSection data
                        keys.forEach((key) => {
                          if (key.startsWith("editSection_")) {
                            localStorage.removeItem(key);
                            console.log(
                              "SectionAppBar: Cleared EditSection data:",
                              key
                            );
                          }
                        });
                      } catch (e) {
                        console.warn(
                          "Failed to clear AddSection/EditSection localStorage:",
                          e
                        );
                      }

                      // Clear preview data keys when navigating to EditSection
                      clearPreviewDataKeys();
                      navigate(
                        `/edit-section/${sectionId}/${partId}/${revisionId}/${partNumber}/${revision}/${
                          title.toLowerCase().includes("subs")
                            ? selectedSubstepTitle
                            : title
                        }`,
                        {
                          state: {
                            data: {
                              siblingOrder: siblingOrder || data.siblingOrder,
                              ...data,
                            },
                            partType,
                            data,
                            revisionId,
                            revision,
                            partNumber,
                            type: data.partType || partType,
                            siblingOrder: siblingOrder || data.siblingOrder,
                          },
                        }
                      );
                    }}
                  >
                    Edit
                  </button>
                )}

                {isStep && (
                  <button
                    className="appbar-button"
                    onClick={() => {
                      const elementTypeNormalized = elementType
                        .toLowerCase()
                        .replace(/\s+/g, "-");
                      const [first = 0, second = 0] = (siblingOrder ?? "")
                        .toString()
                        .split(",")
                        .map((s) => parseInt(s.trim(), 10));

                      const shouldUseSecond =
                        (isStep && elementTypeNormalized !== "step") ||
                        (isSubstep &&
                          !elementType?.toLowerCase().includes("subs"));

                      const siblingIndex =
                        (shouldUseSecond ? second : first) - 1;

                      try {
                        window.indexedDB.deleteDatabase("canvasElementsDB");
                      } catch (e) {
                        console.warn("Failed to clear canvasElementsDB:", e);
                      }
                      localStorage.removeItem("canvasState");
                      localStorage.removeItem("canvasSnapshot");
                      sessionStorage.removeItem("canvasStateInfo");
                      localStorage.removeItem("short_description");
                      localStorage.removeItem("content");
                      localStorage.removeItem("pageId");
                      localStorage.removeItem("fromMyCanvas");
                      localStorage.removeItem("elementCounts");
                      navigate(
                        `/edit-step/${sectionId}/${partId}/${revisionId}/${stepId}/${substepId}/${partNumber}/${title}/${revision}/${stepTitle}/${section}/${siblingIndex}/${elementType}`,
                        {
                          state: {
                            data: {
                              siblingOrder: siblingOrder || data.siblingOrder,
                              ...data,
                            },
                            partType,
                            data,
                            revisionId,
                            revision,
                            partNumber,
                            siblingOrder:
                              siblingOrder || data.fromStepSiblingOrder,
                          },
                        }
                      );
                    }}
                  >
                    Edit
                  </button>
                )}

                {isSectionElement && (
                  <button
                    className="appbar-button"
                    onClick={() => {
                      const elementTypeNormalized = elementType
                        .toLowerCase()
                        .replace(/\s+/g, "-");
                      const [first = 0, second = 0] = (siblingOrder ?? "")
                        .toString()
                        .split(",")
                        .map((s) => parseInt(s.trim(), 10));

                      const shouldUseSecond =
                        (isStep && elementTypeNormalized !== "step") ||
                        (isSubstep &&
                          !elementType?.toLowerCase().includes("subs"));

                      const siblingIndex =
                        (shouldUseSecond ? second : first) - 1;

                      try {
                        window.indexedDB.deleteDatabase("canvasElementsDB");
                      } catch (e) {
                        console.warn("Failed to clear canvasElementsDB:", e);
                      }
                      localStorage.removeItem("canvasState");
                      localStorage.removeItem("canvasSnapshot");
                      sessionStorage.removeItem("canvasStateInfo");
                      localStorage.removeItem("canvas_name");
                      localStorage.removeItem("canvas_category_name");
                      localStorage.removeItem("canvas_category_id");
                      localStorage.removeItem("short_description");
                      localStorage.removeItem("content");
                      localStorage.removeItem("category_id");
                      localStorage.removeItem("category_name");
                      localStorage.removeItem("attachments");
                      localStorage.removeItem("video");
                      localStorage.removeItem("video_url");
                      localStorage.removeItem("pageId");
                      localStorage.removeItem("fromMyCanvas");
                      localStorage.removeItem("elementCounts");
                      window.indexedDB.deleteDatabase("VideoStorageDB");

                      // Clear bulletproof video data when editing a video element
                      if (elementType.toLowerCase() === "video") {
                        console.log(
                          "SectionAppBar: Clearing bulletproof video data for edit video element"
                        );

                        // Clear global video backup
                        if (window.videoBackup) {
                          delete window.videoBackup;
                          console.log(
                            "SectionAppBar: Cleared window.videoBackup for edit"
                          );
                        }

                        // Clear Redux video data (if store is available)
                        if (window.__REDUX_STORE__) {
                          try {
                            window.__REDUX_STORE__.dispatch({
                              type: "video/clearTempVideoData",
                            });
                            console.log(
                              "SectionAppBar: Cleared Redux video data for edit"
                            );
                          } catch (e) {
                            console.warn(
                              "SectionAppBar: Could not clear Redux video data for edit:",
                              e
                            );
                          }
                        }

                        // Clear all bulletproof video localStorage keys
                        const keys = Object.keys(localStorage);
                        keys.forEach((key) => {
                          if (
                            key.startsWith("BULLETPROOF_VIDEO_") ||
                            key === "CURRENT_VIDEO_SESSION"
                          ) {
                            localStorage.removeItem(key);
                            console.log(
                              "SectionAppBar: Cleared bulletproof video key for edit:",
                              key
                            );
                          }
                        });
                      }

                      const cleanElementType = elementType.toLowerCase().replace(/\s+/g, "-");
                      console.log("🔍 Edit navigation params:", {
                        elementType,
                        cleanElementType,
                        sectionId,
                        partId,
                        revisionId,
                        stepId,
                        substepId,
                        partNumber,
                        title,
                        revision,
                        stepTitle,
                        section,
                        siblingIndex,
                        id
                      });
                      
                      navigate(
                        `/edit-${cleanElementType}/${sectionId}/${partId}/${revisionId}/${stepId}/${substepId}/${partNumber}/${title}/${revision}/${stepTitle}/${section}/${siblingIndex}/${cleanElementType}/${id}`,
                        {
                          state: {
                            data: {
                              siblingOrder: siblingOrder || data.siblingOrder,
                              ...data,
                            },
                            partNumbers: data?.partNumbers,
                            partType,
                            data,
                            revisionId,
                            revision,
                            partNumber,
                            siblingOrder:
                              siblingOrder || data.elementSiblingOrder,
                          },
                        }
                      );
                    }}
                  >
                    Edit
                  </button>
                )}
              </>
            )}

            {editSubstepbtn && (
              <button className="appbar-button" onClick={editSubstepbtn}>
                Edit
              </button>
            )}
          </h1>
        </div>

        {/* Right Side: Edit, Move, Copy, Delete Buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {buttons.map((button, index) => (
            <button
              key={index}
              className="appbar-button"
              onClick={button.onClick}
            >
              {button.label}
            </button>
          ))}

          {isSelectedPage && (
            <span>
              {isPart && (
                <button
                  className="move-copy-button"
                  onClick={() => {
                    navigateToSelectProcedureScreen();
                  }}
                >
                  Copy & Replace
                </button>
              )}
              {!isPart && (
                <span>
                  <button
                    className="move-copy-button"
                    onClick={() => {
                      const elementTypeNormalized = elementType
                        .toLowerCase()
                        .replace(/\s+/g, "-");
                      const [first = 0, second = 0] = (siblingOrder ?? "")
                        .toString()
                        .split(",")
                        .map((s) => parseInt(s.trim(), 10));

                      const shouldUseSecond =
                        (isStep && elementTypeNormalized !== "step") ||
                        (isSubstep &&
                          !elementType?.toLowerCase().includes("subs"));

                      const siblingIndex =
                        (shouldUseSecond ? second : first) - 1;

                      // Debug logging to see what values we're working with
                      console.log("DEBUG - Navigation values:", {
                        title,
                        stepTitle,
                        elementType,
                        stepNumber: (() => {
                          const stepMatch = stepTitle.match(/Step\s+(\d+)/i);
                          return stepMatch ? parseInt(stepMatch[1], 10) : null;
                        })(),
                      });

                      navigate(
                        title.includes("Step")
                          ? "/procedure-step-move"
                          : isSection
                          ? "/procedure-section-move"
                          : isSubstep
                          ? "/procedure-substep-move"
                          : elementType.toLowerCase().includes("rich")
                          ? "/procedure-rich-text-move"
                          : elementType.toLowerCase() === "video"
                          ? "/procedure-video-move"
                          : elementType.toLowerCase() === "attachment"
                          ? "/procedure-attachment-move"
                          : elementType.toLowerCase() === "canvas"
                          ? "/procedure-canvas-move"
                          : "/procedure-section-element-move",
                        {
                          state: {
                            ...data,
                            siblingOrder: siblingIndex,
                            elementType,
                            id,
                            title: title.toLowerCase().includes("subs")
                              ? selectedSubstepTitle
                              : title,
                            type: partType || data.type,
                            description,
                            revision,
                            partNumber,
                            status,
                            steps,
                            sourceSubstepIndex: data.sourceSubstepIndex,
                            sourceStepId: data.sourceStepId,
                            sourceNodeId: data.sourceNodeId,
                            targetSubstepIndex: data.targetSubstepIndex,
                            targetStepId: data.targetStepId,
                            targetNodeId: data.targetNodeId,
                            sourceNodeIndex:
                              data.sourceNodeIndex || data.childIndex,
                            sourceSectionIndex: data.sourceSectionIndex,
                            sourceStepIndex: data.stepIndex,
                            // Extract step number from stepTitle for accurate modal heading
                            stepNumber: (() => {
                              // Try to extract from stepTitle first
                              let stepMatch = stepTitle.match(/Step\s+(\d+)/i);
                              if (stepMatch && stepMatch[1]) {
                                const extractedNumber = parseInt(
                                  stepMatch[1],
                                  10
                                );
                                console.log(
                                  "DEBUG - stepTitle:",
                                  stepTitle,
                                  "extracted stepNumber:",
                                  extractedNumber
                                );
                                return extractedNumber;
                              }

                              // Fallback: try to extract from title prop
                              stepMatch = title.match(/Step\s+(\d+)/i);
                              if (stepMatch && stepMatch[1]) {
                                const extractedNumber = parseInt(
                                  stepMatch[1],
                                  10
                                );
                                console.log(
                                  "DEBUG - title:",
                                  title,
                                  "extracted stepNumber:",
                                  extractedNumber
                                );
                                return extractedNumber;
                              }

                              // Final fallback: use data.stepIndex + 1
                              const fallbackNumber =
                                data.stepIndex !== undefined
                                  ? data.stepIndex + 1
                                  : null;
                              console.log(
                                "DEBUG - Using fallback stepNumber:",
                                fallbackNumber,
                                "from data.stepIndex:",
                                data.stepIndex
                              );
                              return fallbackNumber;
                            })(),
                            revisionId,
                            fromTreeId,
                            fromSectionId: sectionId,
                            fromSiblingOrder: data.fromSiblingOrder,
                            partId,
                            fromStepId: stepId,
                            fromStepSiblingOrder:
                              fromStepSiblingOrder || data.fromStepSiblingOrder,
                            fromStepTreeId,
                            fromSubstepTreeId,
                            fromSubstepId: substepId,
                            fromSubstepSiblingOrder:
                              fromSubstepSiblingOrder ||
                              data.sibling_order ||
                              data.procedure_tree?.sibling_order,
                            elementId:
                              data.newData?.rich_text_node_id ||
                              data.rich_text_node_id ||
                              data.newData?.attachment_tree_node_id ||
                              data.attachment_tree_node_id ||
                              data.newData?.video_tree_node_id ||
                              data.video_tree_node_id ||
                              data.newData?.video_tree_node_ref ||
                              data.video_tree_node_ref ||
                              data.newData?.canvas_tree_node_id ||
                              data.canvas_tree_node_id,
                            elementTreeId:
                              data.newData?.procedure_tree_ref ||
                              data.procedure_tree_ref,
                            richTextsByStep: data.richTextsByStep,
                            canvasesByStep: data.canvasesByStep,
                            move_rev_node_info:
                              data.move_rev_node_info || move_rev_node_info,
                          },
                        }
                      );
                    }}
                  >
                    Move
                  </button>
                  <button
                    className="move-copy-button"
                    onClick={() => {
                      navigateToSelectProcedureScreen();
                    }}
                  >
                    Copy
                  </button>
                  <button
                    className="delete-button"
                    onClick={() => {
                      const elementTypeNormalized = elementType
                        .toLowerCase()
                        .replace(/\s+/g, "-");
                      const [first = 0, second = 0] = (siblingOrder ?? "")
                        .toString()
                        .split(",")
                        .map((s) => parseInt(s.trim(), 10));

                      const shouldUseSecond =
                        (isStep && elementTypeNormalized !== "step") ||
                        (isSubstep &&
                          !elementType?.toLowerCase().includes("subs"));

                      const siblingIndex =
                        (shouldUseSecond ? second : first) - 1;
                      const deleteRoute = title.includes("Step")
                          ? "/procedure-step-delete"
                          : isSection
                          ? "/procedure-section-delete"
                          : isSubstep
                          ? "/procedure-substep-delete"
                          : "/procedure-child-delete";
                      
                      // Determine deleteType based on route
                      const deleteType = title.includes("Step")
                          ? "STEP"
                          : isSection
                          ? "SECTION"
                          : isSubstep
                          ? "SUBSTEP"
                          : "CHILD";

                      navigate(deleteRoute, {
                          state: {
                            deleteType, // ✅ ADD THIS
                            siblingOrder: siblingIndex,
                            actualSubstepId: substepId,
                            elementType,
                            id,
                            title: title.toLowerCase().includes("subs")
                              ? selectedSubstepTitle
                              : title,
                            type: partType || data.type,
                            item_code_type: item_code_type,
                            description,
                            revision,
                            partNumber,
                            status: status,
                            steps,
                            sourceSubstepIndex: data.sourceSubstepIndex,
                            sourceStepId: data.sourceStepId,
                            sourceNodeId: data.sourceNodeId,
                            targetSubstepIndex: data.targetSubstepIndex,
                            targetStepId: data.targetStepId,
                            targetNodeId: data.targetNodeId,
                            sourceNodeIndex:
                              data.sourceNodeIndex || data.childIndex,
                            sourceSectionIndex: data.sourceSectionIndex,
                            sourceStepIndex: data.stepIndex,
                            revisionId,
                            fromTreeId,
                            fromSectionId: sectionId,
                            fromSiblingOrder: data.fromSiblingOrder,
                            partId,
                            fromStepId:
                              isSubstep && data?.parentSubstepId
                                ? data.parentSubstepId
                                : stepId,
                            fromStepSiblingOrder:
                              fromStepSiblingOrder || data.fromStepSiblingOrder,
                            fromStepTreeId,
                            fromSubstepTreeId,
                            fromSubstepId: substepId,
                            fromSubstepSiblingOrder:
                              fromSubstepSiblingOrder ||
                              data.sibling_order ||
                              data.procedure_tree?.sibling_order,
                            elementId:
                              data.newData?.rich_text_node_id ||
                              data.rich_text_node_id ||
                              data.newData?.attachment_tree_node_id ||
                              data.attachment_tree_node_id ||
                              data.newData?.video_tree_node_id ||
                              data.video_tree_node_id ||
                              data.newData?.video_tree_node_ref ||
                              data.video_tree_node_ref ||
                              data.newData?.canvas_tree_node_id ||
                              data.canvas_tree_node_id,
                            elementTreeId:
                              data.newData?.procedure_tree_ref ||
                              data.procedure_tree_ref,
                            section: data.section,
                            richTextsByStep: data.richTextsByStep,
                            attachmentsByStep: data.attachmentsByStep,
                            videosByStep: data.videosByStep,
                            canvasesByStep: data.canvasesByStep,
                            formsByStep: data.formsByStep,
                            step: data.step,
                            substep: data.substep,
                            richTexts: data.richTexts,
                            forms: data.forms,
                            attachments: data.attachments,
                            videos: data.videos,
                            canvases: data.canvases,
                            node: data.node,
                            nodeType: data.nodeType,
                            fromNodeId:
                              data.newData?.attachment_tree_node_id ||
                              data.newData?.rich_text_node_id ||
                              data.newData?.video_tree_node_id ||
                              data.newData?.canvas_tree_node_id ||
                              data.fromNodeId,
                            richTexts:
                              data.richTexts || data.richTextsBySubstep,
                            forms: data.forms || data.formsBySubstep,
                            attachments:
                              data.attachments || data.attachmentsBySubstep,
                            videos: data.videos || data.videosBySubstep,
                            canvases: data.canvases || data.canvasesBySubstep,
                            substepsByStep: data.substepsByStep,
                            move_rev_node_info: move_rev_node_info,
                          },
                        }
                      );
                    }}
                  >
                    Delete
                  </button>
                </span>
              )}
            </span>
          )}
        </div>
      </header>

      {showTasks && <TaskPopup closeModal={setShowTasks} />}
    </>
  );
};

export default SectionAppBar;
